
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface DangerZoneProps {
  logout: () => Promise<void>;
}

const DangerZone = ({ logout }: DangerZoneProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    if (confirmText !== "EXCLUIR") {
      toast({
        title: "Erro",
        description: "Por favor, digite EXCLUIR para confirmar",
        variant: "destructive",
      });
      return;
    }

    if (!password) {
      toast({
        title: "Erro",
        description: "Por favor, digite sua senha",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      // First verify the password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || "",
        password,
      });

      if (signInError) {
        throw new Error("Senha incorreta");
      }

      // Delete the user account
      const { error: deleteError } = await supabase.auth.admin.deleteUser(
        (await supabase.auth.getUser()).data.user?.id || ""
      );

      if (deleteError) {
        throw deleteError;
      }

      // Close the dialog and logout
      setDeleteDialogOpen(false);
      
      toast({
        title: "Conta excluída",
        description: "Sua conta foi excluída com sucesso",
      });
      
      // Logout after deletion
      await logout();
      
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível excluir a conta",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-brand-coral">Zona de Perigo</CardTitle>
          <CardDescription>
            Ações irreversíveis para a sua conta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium">Sair da conta</p>
              <p className="text-sm text-gray-500">Encerre sua sessão atual</p>
            </div>
            <Button variant="outline" className="text-brand-coral" onClick={handleLogout}>
              Sair
            </Button>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium">Excluir conta</p>
              <p className="text-sm text-gray-500">Exclua permanentemente sua conta e dados</p>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              Excluir Conta
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conta</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente sua conta e removerá seus dados dos nossos servidores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password">Sua senha</Label>
              <Input 
                id="password" 
                type="password" 
                placeholder="Digite sua senha atual" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm">
                Digite <span className="font-bold">EXCLUIR</span> para confirmar
              </Label>
              <Input 
                id="confirm" 
                placeholder="EXCLUIR" 
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Excluir conta"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DangerZone;
